/**
 * TRAD-3790 — Android «애플로 로그인» 웹 콜백(GET 중계 화면).
 *
 * 흐름(팀장 결정 2026-10-20 «backend 바운서»):
 *   앱 Custom Tab → appleid.apple.com/auth/authorize (client_id = 웹 Service ID, scope=email, response_mode=form_post,
 *   redirect_uri = https://{host}/api/auth/apple/android-callback)
 *   → Apple 이 backend 로 POST(code·state·[user]·[error])
 *   → backend 303 → `GET https://{host}/auth/apple/android-callback?code=…&state=…` (실패면 `?error=…&state=…`)
 *     backend 는 user 를 Location 에 싣지 않기로 했다(팀장 2026-10-20). 실려 와도 이 페이지는 딥링크로 넘기지 않고 주소창에서 지운다.
 *   → 이 페이지가 같은 값을 앱 딥링크로 넘긴다 → 앱이 appleVerify 를 직접 호출한다.
 *
 * 🔴 web 이 POST 를 직접 못 받는 이유: 운영·dev nginx 가 이 경로 POST 에 `405 Not Allowed`
 *   (2026-10-20 실측 `curl -X POST https://{dev.,}xtrading.kr/auth/apple/android-callback`). scope=email 이 필수라
 *   (backend 가 email 로 계정 매칭 — AuthServiceImpl getOrCreateUser) Apple 은 form_post 만 허용한다 → backend 가 받는다.
 *
 * 🔴 `code`(일회용 자격증명)·`user`(이름·이메일 JSON)는 로그·GA·화면에 남기지 않는다.
 *   Firebase Analytics 는 `@/shared/configs/firebase` 가 import 되는 순간(동의한 재방문자) 초기화되고,
 *   gtag 기본 page_view 와 `FirebaseAnalyticsTracker` 둘 다 현재 URL(쿼리 포함)을 보낸다.
 *   그래서 이 모듈의 `captureAppleAndroidCallback` 을 **main.tsx 의 첫 import** 로 돌려, 그 어떤 계측보다 먼저
 *   쿼리를 메모리로 옮기고 주소창에서 지운다(history.replaceState). 이후 계측이 보는 URL 은 경로뿐이다.
 */

export const APPLE_ANDROID_CALLBACK_PATH = '/auth/apple/android-callback';

/**
 * 앱이 받는 딥링크(android 와 합의한 계약 — 바꾸면 앱 intent-filter 도 같이 바뀌어야 한다).
 * dev 앱(hw.dp.xtrading.dev)과 운영 앱이 한 기기에 같이 깔리므로 스킴을 나눈다:
 * dev web(dev.xtrading.kr·로컬) → dev 스킴, 그 밖(운영 xtrading.kr·www) → 운영 스킴.
 */
export const APPLE_ANDROID_DEEP_LINK_BASE = 'xtrading://auth/apple';
export const APPLE_ANDROID_DEV_DEEP_LINK_BASE = 'xtradingdev://auth/apple';
const DEV_HOSTS = new Set(['dev.xtrading.kr', 'localhost', '127.0.0.1']);

export const resolveAppleAndroidDeepLinkBase = (hostname: string): string =>
  DEV_HOSTS.has(hostname.toLowerCase()) ? APPLE_ANDROID_DEV_DEEP_LINK_BASE : APPLE_ANDROID_DEEP_LINK_BASE;

export type AppleAndroidCallbackResult =
  | { kind: 'success'; deepLink: string }
  | { kind: 'error'; deepLink: string }
  | { kind: 'invalid' };

const nonEmpty = (value: string | null): value is string => value !== null && value.trim() !== '';

/**
 * 순수 함수 — 콜백 쿼리 → 앱에 넘길 딥링크.
 * - code 있음 → `?code=…&state=…` (state 는 있으면 그대로; 검증은 앱 몫)
 * - 🔴 `user`(Apple 첫 로그인 이름·이메일 JSON)는 와도 딥링크에 싣지 않는다 — 팀장 결정 2026-10-20(개인정보 최소화:
 *   앱은 code·state·error 만 읽고 appleVerify 는 code 만 받는다). 주소창에서는 쿼리째 지운다(아래 capture).
 * - error 있음 → `?error=…&state=…` (error 가 code 보다 우선 — 둘 다 오면 실패로 본다)
 * - 둘 다 없음 → invalid (딥링크를 만들지 않는다: 빈 code 를 앱이 성공으로 오해하지 않게)
 */
export const buildAppleAndroidDeepLink = (
  search: string,
  base: string = APPLE_ANDROID_DEEP_LINK_BASE,
): AppleAndroidCallbackResult => {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  const out = new URLSearchParams();
  if (nonEmpty(error)) {
    out.set('error', error);
    if (nonEmpty(state)) out.set('state', state);
    return { kind: 'error', deepLink: `${base}?${out.toString()}` };
  }
  if (nonEmpty(code)) {
    out.set('code', code);
    if (nonEmpty(state)) out.set('state', state);
    return { kind: 'success', deepLink: `${base}?${out.toString()}` };
  }
  return { kind: 'invalid' };
};

let captured: AppleAndroidCallbackResult | null = null;

interface CaptureEnv {
  location: Pick<Location, 'pathname' | 'search' | 'hash' | 'hostname'>;
  history: Pick<History, 'replaceState' | 'state'>;
}

/**
 * 부수효과 경계 — 콜백 경로일 때만: 쿼리를 메모리로 옮기고 주소창에서 지운다.
 * 콜백 경로가 아니면 아무것도 안 한다(다른 화면 URL 은 절대 건드리지 않는다).
 */
export const captureAppleAndroidCallback = (env: CaptureEnv): void => {
  const pathname = env.location.pathname.replace(/\/+$/, '');
  if (pathname !== APPLE_ANDROID_CALLBACK_PATH) return;
  captured = buildAppleAndroidDeepLink(
    env.location.search,
    resolveAppleAndroidDeepLinkBase(env.location.hostname),
  );
  if (env.location.search !== '') {
    env.history.replaceState(env.history.state, '', APPLE_ANDROID_CALLBACK_PATH + env.location.hash);
  }
};

export const getCapturedAppleAndroidCallback = (): AppleAndroidCallbackResult =>
  captured ?? { kind: 'invalid' };

/** 테스트 전용. */
export const resetCapturedAppleAndroidCallback = (): void => {
  captured = null;
};
